import { useSyncExternalStore } from 'react';

function setLocalStorageState<TValue>(key: string, value: TValue) {
  const stringifiedValue = JSON.stringify(value);
  const oldValue = localStorage.getItem(key);

  window.localStorage.setItem(key, stringifiedValue);
  window.dispatchEvent(
    new StorageEvent('storage', { key, oldValue, newValue: stringifiedValue })
  );
}

function removeLocalStorageState(key: string) {
  const oldValue = localStorage.getItem(key);
  window.localStorage.removeItem(key);
  window.dispatchEvent(new StorageEvent('storage', { key, oldValue, newValue: null }));
}

export const useLocalStorage = <T>(
  key: string,
  defaultValue?: T
): [T | null, (value: T) => void, () => void] => {
  const subscribe = (listener: (event: StorageEvent) => any) => {
    window.addEventListener('storage', listener);
    return () => void window.removeEventListener('storage', listener);
  };

  const getSnapshot = <T>(key: string): T | null => {
    const storedValue = window.localStorage.getItem(key);

    if (!storedValue) {
      return (defaultValue as T | null) ?? null;
    }

    try {
      return JSON.parse(storedValue);
    } catch (error) {
      console.warn(`Failed to parse localStorage value for key "${key}":`, error);
      // Remove the invalid value from localStorage
      window.localStorage.removeItem(key);
      return (defaultValue as T | null) ?? null;
    }
  };

  const state = useSyncExternalStore<T | null>(
    subscribe,
    () => getSnapshot(key),
    () => null
  );

  return [
    state,
    (value: T) => setLocalStorageState(key, value),
    () => removeLocalStorageState(key),
  ] as const;
};
